import type { ReactNode } from 'react';
import { LogoPrixia } from '@/components/layout/logo-prixia';
import { Link } from 'react-router-dom';
import { CircleHelp } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

/**
 * El marco de las pantallas sin sesión (ingreso, olvidé mi contraseña, nueva
 * contraseña), según la maqueta de Melanie del 25-oct: barra blanca con el logo
 * y la ayuda, lienzo lavanda y una sola tarjeta al centro.
 *
 * Sin «Plataforma activa», versión ni «Portal corporativo»: Joaquín los sacó
 * porque son adorno y no le dicen nada a quien entra.
 */
export function MarcoDeIngreso({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-lienzo font-interfaz">
      <header className="border-b border-border bg-background">
        <div className="flex h-16 w-full items-center justify-between px-4 sm:h-[76px] sm:px-9">
          <Link to="/" aria-label="Prixia, ir al inicio" className="flex items-center">
            <LogoPrixia className="h-8 w-auto sm:h-10" />
          </Link>
          <Link
            to="/docs/help"
            className="inline-flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <CircleHelp className="size-4" aria-hidden="true" />
            Centro de ayuda
          </Link>
        </div>
      </header>
      <main className="flex flex-1 justify-center px-4 pb-16 pt-12 sm:pt-20">
        <div className="flex w-full max-w-[560px] flex-col gap-8">{children}</div>
      </main>
    </div>
  );
}

/** El título grande de arriba de la tarjeta, con la parte de marca en degradado. */
export function TituloDeIngreso({
  antes,
  marca,
  subtitulo,
}: {
  antes: string;
  marca?: string;
  subtitulo: string;
}) {
  return (
    <div className="flex flex-col items-center gap-3 text-center">
      <h1 className="font-titulo text-[34px] font-semibold leading-tight tracking-tight text-foreground sm:text-[42px]">
        {antes}
        {marca && (
          <>
            {' '}
            <span className="bg-degradado-prixia-texto bg-clip-text text-transparent">{marca}</span>
          </>
        )}
      </h1>
      <p className="text-base text-muted-foreground sm:text-[17px]">{subtitulo}</p>
    </div>
  );
}

/** La tarjeta blanca del formulario. */
export function TarjetaDeIngreso({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'rounded-[28px] border border-border bg-card p-7 shadow-[0_24px_60px_-28px_hsl(265_60%_30%/0.18)] sm:p-12',
        className
      )}
    >
      {children}
    </div>
  );
}

/** Rótulo de campo en mayúsculas chicas, como la maqueta. */
export const ROTULO_DE_CAMPO =
  'text-[13px] font-semibold uppercase tracking-[0.04em] text-foreground/85';

/** Campo alto y redondeado, con lugar para un ícono a la izquierda. */
export const CAMPO_DE_INGRESO = 'h-12 rounded-xl bg-lienzo pl-11 text-[15px]';

/** Posición de los íconos dentro del campo (centrados en sus 48 px). */
export const ICONO_DE_CAMPO =
  'pointer-events-none absolute left-4 top-6 size-[18px] -translate-y-1/2 text-muted-foreground';
